import type { TrialMomento } from '@/shared/components/ui/common/AvisoTrialModal';

import type { ContractCategory, MarcoTeste, TrialStatusItem } from './types';
import { formatarDiaMes } from './utils/periodoContrato';
import { capitalizarPrimeiraLetra } from '@/shared/utils/text';

import {
  acaoBloqueadaDaCategoria,
  flexoesDaCategoria,
  nomeDaCategoria,
  unidadeDaCategoria,
} from './utils/rotulosCategoria';

export interface TrialAvisoView {
  categoria: ContractCategory;
  produtoNome: string;
  momento: TrialMomento;
  dataFim: string;
  diasRestantes: number;
  acaoBloqueada: string;
  itensPreservados?: string;
  usadoNoTeste?: number;
  unidadeLabel?: string;
  possessivoLabel?: string;
}

const MOMENTO_POR_MARCO: Record<MarcoTeste, TrialMomento> = {
  D7: 'faltam7',
  D3: 'faltam3',
  ULTIMO_DIA: 'ultimoDia',
  ENCERRADO: 'terminou',
};

/**
 * Converte o item do endpoint `trial-status` na view do aviso.
 * O marco vem resolvido do servidor — a mesma função pura que decide o e-mail —,
 * então a tela nunca pode discordar da mensagem enviada.
 */
export function avisoDoTrialStatus(item: TrialStatusItem): TrialAvisoView {
  const usado = item.uso?.usados ?? 0;
  const unidade = unidadeDaCategoria(item.category);
  const { possessivo } = flexoesDaCategoria(item.category);

  return {
    categoria: item.category,
    produtoNome: item.productName ?? nomeDaCategoria(item.category),
    momento: MOMENTO_POR_MARCO[item.marco],
    dataFim: formatarDiaMes(item.trialEnd),
    diasRestantes: Math.max(0, item.diasRestantes ?? 0),
    acaoBloqueada: acaoBloqueadaDaCategoria(item.category),
    itensPreservados:
      usado > 0
        ? `${capitalizarPrimeiraLetra(possessivo)} ${usado} ${unidade} continuam disponíveis para consulta e download`
        : undefined,
    usadoNoTeste: usado,
    unidadeLabel: unidade,
    possessivoLabel: possessivo,
  };
}

const chaveVisto = (categoria: string, momento: TrialMomento) =>
  `trial-aviso-visto:${categoria}:${momento}`;

/** Um mesmo momento só abre o modal uma vez por sessão do navegador. */
export function jaViuAviso(categoria: string, momento: TrialMomento): boolean {
  if (typeof window === 'undefined') return true;
  try {
    return window.sessionStorage.getItem(chaveVisto(categoria, momento)) === '1';
  } catch {
    return false;
  }
}

export function marcarAvisoVisto(categoria: string, momento: TrialMomento): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(chaveVisto(categoria, momento), '1');
  } catch {
    return;
  }
}
